/* eslint-disable camelcase */
import moment from "moment-timezone";
import "moment/locale/sv";
import * as Yup from "yup";
import { Translations } from "../../../localization/Translations";
import { CourseBookingError } from "../../../models/courses/CourseBookingError";
import { FormProps } from "../../../templates/CourseBookingForm";

export enum PayerType {
  Business = "business",
  PrivateIndividual = "privateIndividual"
}

export const getBookingErrorTranslation = (
  error: unknown,
  translations: Translations
): string => {
  let errorTranslation: string | null | undefined = "";

  const isCourseBookingError = (errorMsg: string): errorMsg is CourseBookingError =>
    Object.values<string>(CourseBookingError).includes(errorMsg);
  const getTranslation = (errorMsg: CourseBookingError) => {
    switch (errorMsg) {
      case CourseBookingError.ALREADY_BOOKED:
        return translations?.["alreadyParticipantOfTheCourse"];
      case CourseBookingError.GENERAL:
        return translations?.["error"];
      default: {
        const _check: never = errorMsg;
        console.error(`Unhandled booking error message: ${_check}`);
        return translations?.["error"];
      }
    }
  };

  if (error instanceof Error) {
    if (isCourseBookingError(error.message)) {
      errorTranslation = getTranslation(error.message);
    }
  }

  return errorTranslation || translations?.["error"] || "An error occurred";
};

/**
 * Prepares a phone number by ensuring it starts with the given country code.
 *
 * @param countryCode - The country code without '+' to prepend if not already present.
 * @returns A function that takes an optional phone number string and returns the formatted phone number with '+' symbol and country code.
 *          If the phone number starts with the country code, it is returned as is with a '+' prefix.
 *          Otherwise, the country code is prepended to the phone number with a '+' prefix.
 */
export const preparePhoneNumber =
  (countryCode: string = "") =>
  (phoneNumber: string) => {
    if (!phoneNumber) {
      return "";
    }

    const value = phoneNumber.trim().replace(/[^\d]/g, "");

    return value.startsWith(countryCode) ? `+${value}` : `+${countryCode}${value}`;
  };

// Validation schema
export const getValidationSchema = (
  payerType: PayerType,
  errorMessages: FormProps["errorMessages"],
  agreementCheckboxes: FormProps["agreementCheckboxes"],
  countryCode: FormProps["participantsPhoneCode"]
) => {
  const sharedFields = {
    address: Yup.string().required(errorMessages?.addressRequired),
    postCode: Yup.string().required(errorMessages?.postCodeRequired),
    city: Yup.string().required(errorMessages?.cityRequired),
    emailForBilling: Yup.string().email(errorMessages?.studentEmailInvalid)
  };

  const agreementFields = agreementCheckboxes.reduce((acc, checkbox) => {
    acc[`checkbox-${checkbox.name}`] = Yup.boolean()
      .oneOf([true], errorMessages?.agreementRequired)
      .required(errorMessages?.agreementRequired);
    return acc;
  }, {});

  return Yup.object().shape({
    student: Yup.object().shape({
      firstName: Yup.string()
        .required(errorMessages?.studentFirstNameRequired)
        .min(2, errorMessages?.studentFirstNameMinLength),
      lastName: Yup.string()
        .required(errorMessages?.studentLastNameRequired)
        .min(2, errorMessages?.studentLastNameMinLength),
      email: Yup.string()
        .email(errorMessages?.studentEmailInvalid)
        .required(errorMessages?.studentEmailRequired),
      phone: Yup.string()
        .transform(preparePhoneNumber(countryCode))
        .matches(/^\+[1-9]\d{4,14}$/, errorMessages?.studentPhoneInvalid)
    }),
    ...(payerType === PayerType.Business
      ? {
          companyBookingDetails: Yup.object().shape({
            companyName: Yup.string().required(errorMessages?.companyNameRequired),
            organizationNumber: Yup.string().required(
              errorMessages?.organizationNumberRequired
            ),
            contactPersonsFullName: Yup.string().required(
              errorMessages?.companyContactFullNameRequired
            ),
            contactPersonsEmail: Yup.string()
              .email(errorMessages?.companyContactEmailInvalid)
              .required(errorMessages?.companyContactEmailRequired),
            ...sharedFields
          })
        }
      : {
          individualBookingDetails: Yup.object().shape({
            firstName: Yup.string().required(errorMessages?.individualFirstNameRequired),
            lastName: Yup.string().required(errorMessages?.individualLastNameRequired),
            socialSecurityNumber: Yup.string()
              .matches(/^\d{6}-\d{4}$/, errorMessages?.socialSecurityNumberInvalid)
              .required(errorMessages?.socialSecurityNumberRequired),
            contactPersonsEmail: Yup.string()
              .email(errorMessages?.individualContactEmailInvalid)
              .required(errorMessages?.individualContactEmailRequired),
            ...sharedFields
          })
        }),
    ...agreementFields
  });
};

export const get = (obj, path, defaultValue = "") =>
  path.split(".").reduce((acc, part) => acc && acc[part], obj) ?? defaultValue;

// Helper functions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeKeysFromObject = (obj: any, keysToRemove: string[]) => {
  const newObj = { ...obj };

  keysToRemove.forEach(key => {
    delete newObj[key];
  });

  return newObj;
};

export const getDateRender = (start_date: unknown, end_date: unknown) => {
  if (!start_date || !end_date) {
    return "Invalid arguments";
  }

  const startDate = moment(start_date).locale("sv");
  const endDate = moment(end_date).locale("sv");

  const isSameDay = startDate.isSame(endDate, "day");

  const formattedStartDate = startDate.format("DD MMMM YYYY");
  const startTime = startDate.format("HH:mm");
  const endTime = endDate.format("HH:mm");

  const result = isSameDay
    ? `${formattedStartDate} | ${startTime}-${endTime}`
    : `${formattedStartDate}`;

  return result;
};

export interface FormValues {
  totalSumWithoutVAT: number;
  totalSum: number;
  student: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  companyBookingDetails?: {
    companyName: string;
    organizationNumber: string;
    contactPersonsFullName: string;
    contactPersonsEmail: string;
    address: string;
    postCode: string;
    city: string;
    country: string;
    emailForBilling: string;
    additionalMessage: string;
    campaignCode?: string;
    invoiceReference?: string;
  };
  individualBookingDetails?: {
    firstName: string;
    lastName: string;
    socialSecurityNumber: string;
    contactPersonsEmail: string;
    address: string;
    postCode: string;
    city: string;
    country: string;
    emailForBilling: string;
    additionalMessage: string;
    campaignCode?: string;
    invoiceReference?: string;
  };
}

export interface FormValuesLocalStorageState {
  submissionId: string;
  formValues: FormValues;
  courseId: string;
  subCourseId: string;
}

export const getFormValuesState = (values: FormValues) => ({
  ...values,
  companyBookingDetails: {
    ...values.companyBookingDetails,
    contactPersonEmail:
      values?.companyBookingDetails?.contactPersonsEmail ??
      values?.individualBookingDetails?.contactPersonsEmail,
    address:
      values?.companyBookingDetails?.address ?? values?.individualBookingDetails?.address,
    postCode:
      values?.companyBookingDetails?.postCode ??
      values?.individualBookingDetails?.postCode,
    city: values?.companyBookingDetails?.city ?? values?.individualBookingDetails?.city,
    country:
      values?.companyBookingDetails?.country ?? values?.individualBookingDetails?.country,
    emailForBilling:
      values?.companyBookingDetails?.emailForBilling ??
      values?.individualBookingDetails?.emailForBilling,
    additionalMessage:
      values?.companyBookingDetails?.additionalMessage ??
      values?.individualBookingDetails?.additionalMessage,
    campaignCode:
      values?.companyBookingDetails?.campaignCode ??
      values?.individualBookingDetails?.campaignCode,
    invoiceReference:
      values?.companyBookingDetails?.invoiceReference ??
      values?.individualBookingDetails?.invoiceReference
  },
  individualBookingDetails: {
    ...values.individualBookingDetails,
    contactPersonEmail:
      values?.individualBookingDetails?.contactPersonsEmail ??
      values?.companyBookingDetails?.contactPersonsEmail,
    address:
      values?.individualBookingDetails?.address ?? values?.companyBookingDetails?.address,
    postCode:
      values?.individualBookingDetails?.postCode ??
      values?.companyBookingDetails?.postCode,
    city: values?.individualBookingDetails?.city ?? values?.companyBookingDetails?.city,
    country:
      values?.individualBookingDetails?.country ?? values?.companyBookingDetails?.country,
    emailForBilling:
      values?.individualBookingDetails?.emailForBilling ??
      values?.companyBookingDetails?.emailForBilling,
    additionalMessage:
      values?.individualBookingDetails?.additionalMessage ??
      values?.companyBookingDetails?.additionalMessage,
    campaignCode:
      values?.individualBookingDetails?.campaignCode ??
      values?.companyBookingDetails?.campaignCode,
    invoiceReference:
      values?.individualBookingDetails?.invoiceReference ??
      values?.companyBookingDetails?.invoiceReference
  }
});
